import { StatusMessage, Box, IndexButton, Nowrap } from '@farmersdog/corgi-x';

import styles from './VetTestimonials.module.css';
import { useState } from 'react';
import { useTrackedSectionRef } from 'src/hooks/useTrackedSections';
import { HomepageRedesignSections } from '../CtaTrackerV2';
import { vetTestimonialItems } from './vetTestimonialItems';
import { useInterval } from 'src/hooks/useInterval/useInterval';
import classNames from 'classnames';
import { ChevronButton } from '../PressBarSection/ChevronButton';
import { AnimatePresence, motion } from 'motion/react';
import { VetTestimonialItem } from './VetTestimonialItem';
import { ExtendedText } from '../ExtendedText';
import { CHANGE_ITEM_INTERVAL_SECONDS } from '../PressBarSection/config';

// Ensures that the modulo operation always returns a positive number we can use as array indexes
const safeModulo = (n: number, m: number) => ((n % m) + m) % m;

export const VetTestimonials = () => {
  const [isTriggered, setIsTriggered] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const itemCount = vetTestimonialItems.length;

  const [activeIndex, setActiveIndex] = useState(0);

  // Advance to the next item automatically
  useInterval({
    durationSeconds: CHANGE_ITEM_INTERVAL_SECONDS,
    onTimeLapsed: () => {
      setActiveIndex(prev => prev + 1);
    },
    enabled: isTriggered && !isPaused,
  });

  const activeItem = vetTestimonialItems[safeModulo(activeIndex, itemCount)];

  const vetTestimonialsRef = useTrackedSectionRef<HTMLDivElement>(
    HomepageRedesignSections.VetTestimonials
  );

  const vetTestimonialsId = 'vet-testimonials-id';
  const activeQuoteId = `quote-${activeIndex}`;

  return (
    <Box
      px={24}
      className={classNames(styles.section)}
      ref={vetTestimonialsRef}
      aria-labelledby={vetTestimonialsId}
      as="section"
    >
      <ExtendedText
        variant="heading-36"
        color="Kale3"
        weight="black"
        align="center"
        bottomSpacing="xl"
        id={vetTestimonialsId}
      >
        See what vets <Nowrap>are saying</Nowrap>
      </ExtendedText>
      <div className={classNames(styles.container)}>
        <div className={styles.prevButtonContainer}>
          <ChevronButton
            orientation="left"
            onClick={() => setActiveIndex(prev => prev - 1)}
            ariaLabel="Previous quote"
          />
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={activeItem.author}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: 'easeInOut' }}
            className={styles.slide}
            onViewportEnter={() => {
              setIsTriggered(true);
            }}
            onTouchStart={() => setIsPaused(true)}
            onTouchEnd={() => setIsPaused(false)}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onFocus={() => setIsPaused(true)}
            onBlur={() => setIsPaused(false)}
          >
            <div
              className={classNames(styles.footnoteContainer)}
              onClick={() => setIsPaused(false)}
              tabIndex={0}
            >
              <StatusMessage
                key={activeIndex}
                politeness="polite"
                aria-label={`Quote from ${activeItem.author}`}
                id={activeQuoteId}
              >
                <div aria-hidden className={styles.footnote}>
                  <VetTestimonialItem
                    quote={activeItem.quote}
                    author={activeItem.author}
                  />
                </div>
              </StatusMessage>
            </div>
          </motion.div>
        </AnimatePresence>
        <div className={styles.nextButtonContainer}>
          <ChevronButton
            orientation="right"
            onClick={() => setActiveIndex(prev => prev + 1)}
            ariaLabel="Next quote"
          />
        </div>
      </div>
      <div>
        {vetTestimonialItems.map((item, childIndex) => (
          <IndexButton
            key={item.author}
            variant="kale"
            aria-label={`Go to quote from ${item.author}`}
            aria-controls={activeQuoteId}
            active={safeModulo(activeIndex, itemCount) === childIndex}
            onClick={() => setActiveIndex(childIndex)}
          />
        ))}
      </div>
    </Box>
  );
};
